import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../lib/api.js'
import { ANNEX_A } from './Controls.jsx'
import { FileText, Download, Eye, X, ShieldCheck, Info, Link as LinkIcon, ExternalLink, Briefcase, Server } from 'lucide-react'

function statusColor(s) {
  return {
    'Implemented': { bg: 'rgba(16,185,129,0.15)', color: '#34d399' },
    'In Progress': { bg: 'rgba(245,158,11,0.15)', color: '#fbbf24' },
    'Not Started': { bg: 'rgba(239,68,68,0.15)', color: '#f87171' },
    'Not Applicable': { bg: 'var(--bg-elevated)', color: 'var(--text-muted)' },
  }[s] || { bg: 'var(--bg-elevated)', color: 'var(--text-muted)' }
}

function ControlViewModal({ control, dbControl, documents = [], securityControls = [], processes = [], assets = [], onClose }) {
  const parseJSON = (str) => {
    try { return JSON.parse(str || '[]') } catch { return [] }
  }

  const linkedDocIds = parseJSON(dbControl?.evidence_docs)
  const linkedSecCtrlIds = parseJSON(dbControl?.evidence_controls)
  const linkedProcessIds = parseJSON(dbControl?.evidence_processes)
  const linkedAssetIds = parseJSON(dbControl?.evidence_assets)

  const linkedDocs = documents.filter(d => linkedDocIds.includes(String(d.id)))
  const linkedSecCtrls = securityControls.filter(sc => linkedSecCtrlIds.includes(String(sc.id)))
  const linkedProcesses = processes.filter(p => linkedProcessIds.includes(String(p.id)))
  const linkedAssets = assets.filter(a => linkedAssetIds.includes(String(a.id)))

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 800 }}>
        <div className="modal-header">
          <div>
            <span className="font-mono text-xs" style={{ color: 'var(--accent)', fontWeight: 600 }}>{control.id}</span>
            <h2 style={{ fontSize: '1.25rem', marginTop: 2 }}>{control.name}</h2>
            <span className="badge badge-neutral" style={{ marginTop: 4 }}>{dbControl?.category}</span>
          </div>
          <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ maxHeight: '80vh', overflow: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
            <div className="card shadow-sm" style={{ padding: 16, border: '1px solid var(--border)' }}>
              <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Applicability</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {dbControl?.applicable === 0 ? <X size={16} color="var(--danger)" /> : <ShieldCheck size={16} color="var(--success)" />}
                <span style={{ fontWeight: 500 }}>{dbControl?.applicable === 0 ? 'Not Applicable' : 'Applicable'}</span>
              </div>
            </div>
            <div className="card shadow-sm" style={{ padding: 16, border: '1px solid var(--border)' }}>
              <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Status</span>
              <span className={`badge ${dbControl?.implementation_status === 'Implemented' ? 'badge-success' : dbControl?.implementation_status === 'In Progress' ? 'badge-warning' : 'badge-danger'}`}>
                {dbControl?.implementation_status || 'Not Started'}
              </span>
            </div>
          </div>

          <section style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Info size={16} color="var(--accent)" /> Control Description
            </h4>
            <p style={{ fontSize: '0.875rem', lineHeight: 1.6, color: 'var(--text-secondary)', background: 'var(--bg-base)', padding: 16, borderRadius: 8 }}>
              {dbControl?.description || 'No description available.'}
            </p>
          </section>

          <section style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8 }}>Justification</h4>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', minHeight: 40 }}>
              {dbControl?.justification || <span style={{ fontStyle: 'italic', opacity: 0.5 }}>No justification provided.</span>}
            </div>
          </section>

          <section style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8 }}>Implementation Notes</h4>
            <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', background: 'var(--bg-elevated)', padding: 12, borderRadius: 8, borderLeft: '3px solid var(--border)' }}>
              {dbControl?.notes || <span style={{ fontStyle: 'italic', opacity: 0.5 }}>No notes recorded.</span>}
            </div>
          </section>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <section>
              <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FileText size={14} color="var(--text-muted)" /> Linked Evidence
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {linkedDocs.map(d => (
                  <Link key={d.id} to={`/documents?search=${encodeURIComponent(d.title)}`} className="flex items-center justify-between gap-8" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '6px 10px', background: 'var(--surface)', borderRadius: 6, textDecoration: 'none' }}>
                    <span className="flex items-center gap-6"><FileText size={12} /> {d.title}</span>
                    <ExternalLink size={10} />
                  </Link>
                ))}
                {linkedDocs.length === 0 && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No documents linked.</span>}
              </div>
            </section>
            <section>
              <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <LinkIcon size={14} color="var(--text-muted)" /> Security Controls
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {linkedSecCtrls.map(sc => (
                  <Link key={sc.id} to={`/controls-library?search=${encodeURIComponent(sc.name)}`} className="flex items-center justify-between gap-8" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '6px 10px', background: 'var(--accent-dim)', borderRadius: 6, textDecoration: 'none' }}>
                    <span className="flex items-center gap-6"><ShieldCheck size={12} color="var(--accent)" /> {sc.name}</span>
                    <ExternalLink size={10} />
                  </Link>
                ))}
                {linkedSecCtrls.length === 0 && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No security controls linked.</span>}
              </div>
            </section>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }}>
            <section>
              <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Briefcase size={14} color="var(--text-muted)" /> Linked Processes
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {linkedProcesses.map(p => (
                  <Link key={p.id} to={`/processes?search=${encodeURIComponent(p.process_name)}`} className="flex items-center justify-between gap-8" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '6px 10px', background: 'var(--surface)', borderRadius: 6, textDecoration: 'none' }}>
                    <span className="flex items-center gap-6"><Briefcase size={12} /> {p.process_name}</span>
                    <ExternalLink size={10} />
                  </Link>
                ))}
                {linkedProcesses.length === 0 && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No processes linked.</span>}
              </div>
            </section>
            <section>
              <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Server size={14} color="var(--text-muted)" /> Linked Assets
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {linkedAssets.map(a => (
                  <Link key={a.id} to={`/assets?search=${encodeURIComponent(a.asset_name)}`} className="flex items-center justify-between gap-8" style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', padding: '6px 10px', background: 'var(--surface)', borderRadius: 6, textDecoration: 'none' }}>
                    <span className="flex items-center gap-6"><Server size={12} color="var(--accent)" /> {a.asset_name}</span>
                    <ExternalLink size={10} />
                  </Link>
                ))}
                {linkedAssets.length === 0 && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>No assets linked.</span>}
              </div>
            </section>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

export function SoA({ toast }) {
  const [viewing, setViewing] = useState(null)
  const { data: dbControls = [], isLoading } = useQuery({ 
    queryKey: ['annex-controls'], 
    queryFn: api.getAnnexControls 
  })
  const { data: documents = [] } = useQuery({ queryKey: ['documents'], queryFn: api.getDocuments })
  const { data: securityControls = [] } = useQuery({ queryKey: ['security-controls'], queryFn: api.getSecurityControls })
  const { data: processes = [] } = useQuery({ queryKey: ['processes'], queryFn: api.getProcesses })
  const { data: assets = [] } = useQuery({ queryKey: ['assets'], queryFn: api.getAssets })

  const getDb = (id) => dbControls.find(c => c.control_id === id)
  const getStatus = (id) => {
    const db = getDb(id)
    if (!db) return 'Not Started'
    if (db.applicable === 0) return 'Not Applicable'
    return db.implementation_status || 'Not Started'
  }

  const allControls = ANNEX_A.flatMap(d => d.controls.map(c => ({ ...c, domain: d.domain })))
  const applicable = allControls.filter(c => getStatus(c.id) !== 'Not Applicable')
  const implemented = applicable.filter(c => getStatus(c.id) === 'Implemented')

  const handleExport = () => {
    const rows = [['Control ID', 'Control Name', 'Domain', 'Applicable', 'Implementation Status', 'Justification', 'Notes']]
    allControls.forEach(c => {
      const db = getDb(c.id)
      rows.push([
        c.id, c.name, c.domain,
        db?.applicable === 0 ? 'No' : 'Yes',
        getStatus(c.id),
        db?.justification || '',
        db?.notes || '', // Annex controls have a notes field too
      ])
    })
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'SoA_ISO27001_2022.csv'; a.click()
    URL.revokeObjectURL(url)
    toast('SoA exported as CSV', 'success')
  }

  if (isLoading) return <div className="page"><p className="text-muted">Loading…</p></div>

  return (
    <div className="page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1>Statement of Applicability</h1>
          <p>ISO 27001:2022 — Clause 6.1.3 — All 93 Annex A controls</p>
        </div>
        <button className="btn btn-secondary" onClick={handleExport}>
          <Download size={15} /> Export CSV
        </button>
      </div>

      {/* Summary */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        <div className="stat-card" style={{ padding: '16px 20px' }}>
          <div className="stat-label">Total Controls</div>
          <div className="stat-value" style={{ fontSize: '1.5rem' }}>93</div>
        </div>
        <div className="stat-card" style={{ padding: '16px 20px' }}>
          <div className="stat-label">Applicable</div>
          <div className="stat-value" style={{ fontSize: '1.5rem', color: 'var(--info)' }}>{applicable.length}</div>
        </div>
        <div className="stat-card" style={{ padding: '16px 20px' }}>
          <div className="stat-label">Implemented</div>
          <div className="stat-value" style={{ fontSize: '1.5rem', color: 'var(--accent)' }}>{implemented.length}</div>
        </div>
        <div className="stat-card" style={{ padding: '16px 20px' }}>
          <div className="stat-label">Coverage</div>
          <div className="stat-value" style={{ fontSize: '1.5rem', color: 'var(--accent)' }}>
            {applicable.length ? Math.round((implemented.length / applicable.length) * 100) : 0}%
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Control ID</th>
              <th>Control Name</th>
              <th>Domain</th>
              <th>Applicable</th>
              <th>Status</th>
              <th>Justification / Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {allControls.map(c => {
              const db = getDb(c.id)
              const status = getStatus(c.id)
              const sc = statusColor(status)
              return (
                <tr key={c.id} style={{ opacity: db?.applicable === 0 ? 0.5 : 1 }}>
                  <td>
                    <span className="font-mono" style={{ fontSize: '0.8125rem', color: 'var(--accent)', fontWeight: 600 }}>{c.id}</span>
                  </td>
                  <td style={{ fontSize: '0.875rem', fontWeight: 500, maxWidth: 280 }}>
                    <span className="truncate" style={{ display: 'block' }}>{c.name}</span>
                  </td>
                  <td>
                    <span className="badge badge-neutral" style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                      {c.domain.split('.')[0].trim()}
                    </span>
                  </td>
                  <td>
                    {db?.applicable === 0
                      ? <span className="badge badge-neutral">No</span>
                      : <span className="badge badge-success">Yes</span>
                    }
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 9px', borderRadius: 100, fontSize: '0.75rem', fontWeight: 500, background: sc.bg, color: sc.color }}>
                      {status}
                    </span>
                  </td>
                  <td style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', maxWidth: 300 }}>
                    <span className="truncate" style={{ display: 'block' }} title={db?.justification || db?.notes}>
                      {db?.justification || db?.notes || '—'}
                    </span>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <button className="btn btn-ghost btn-sm" style={{ padding: 6 }} onClick={() => setViewing({ control: c, dbControl: db })}>
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {viewing && (
        <ControlViewModal
          control={viewing.control}
          dbControl={viewing.dbControl}
          documents={documents}
          securityControls={securityControls}
          processes={processes}
          assets={assets}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  )
}
